import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Keep Tab inside `ref` while `active`, and hand focus back to whatever opened
 * it on close.
 *
 * Escape and an initial focus() are not a focus trap on their own: Tab still
 * walks out of the panel and onto the page behind it, where a keyboard user
 * is then operating a UI they cannot see. This closes that loop.
 */
export function useFocusTrap(ref, active) {
  useEffect(() => {
    if (!active || !ref.current) return;

    const root = ref.current;
    const opener = document.activeElement;

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const items = Array.from(root.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      // Wrap in both directions, and pull focus back in if it has already
      // escaped — which happens when the panel's contents change while open.
      if (e.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [ref, active]);
}
